// @flow

import * as React from 'react';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectCartProductOwner,
  type CartBundle,
  type DecoratedProduct,
  type ProductKind as TProductKind,
} from '@gandi/cart';
import { Base } from '@gandi/ui';
import { List as ListLegacy } from '@gandi/ui/deprecated';

import { useAppSelector } from 'src/hooks';
import { webhostingSizes } from 'src/modules/simplehosting/prices';

import ProductsList from './ProductsList';
import { DomainBundleRow } from './components/DomainBundleRow';
import DomainMiscFeesRow from './components/DomainMiscFeesRow';
import ProductKindHeader from './components/ProductKindHeader';
import { ProductContext } from './modules/ProductContext';
import {
  selectProductsByKind,
  selectCorporateMiscFeesByKind,
  selectDomainBundles,
} from './selectors';
import { getProductComponent } from './tools';

import styles from './ProductKind.scss';

type Props = { kind: TProductKind };

const getBundleKey = (bundle: CartBundle) => `bundle-${bundle.id}`;

// Prevent re-rendering of mailbox product-components when adding/removing
// a mailbox single product.
// Mailbox products are abstracted into the redux state and `product.id`
// changes as the quantity of these products do.
const getProductKey = (product: DecoratedProduct): string =>
  product.productType === 'mailboxv2' &&
  product.process === 'create' &&
  product.payload.fqdn &&
  product.payload.mailbox_type
    ? `mailboxv2-${product.payload.fqdn}-${product.payload.mailbox_type}`
    : `${product.id}`;

export function ProductKind({ kind }: Props): React.MixedElement | null {
  const productsByKind = useAppSelector(selectProductsByKind);
  const authenticated = useAppSelector(selectIsAuthenticatedUser);
  const cartDomainBundles = useAppSelector(selectDomainBundles);
  const cartProductOwner = useAppSelector(selectCartProductOwner);
  const corporateMiscFeesByKind = useAppSelector(selectCorporateMiscFeesByKind);

  const showDomainBundles =
    kind === 'DomainCreate' && !!cartDomainBundles.length;

  const products: void | Array<DecoratedProduct> = productsByKind[kind];

  if (!products) {
    return null;
  }

  const productItems = products.filter(product => !product.bundleId);

  const firstProduct = products[0];
  const miscFees = corporateMiscFeesByKind[kind];

  const hasWebhosting =
    firstProduct.productType === 'paas' &&
    productItems.find(p => webhostingSizes.includes(p.payload?.size)) !==
      undefined;

  /// XXX: needed cause of the ListLegacy
  const renderDomainBundle: (bundle: CartBundle) => React.MixedElement = (
    bundle: CartBundle,
  ) => <DomainBundleRow bundle={bundle} />;

  return (
    <ProductKindBlock id={`section-${kind}`}>
      <ProductKindHeader
        key={kind}
        kind={kind}
        process={firstProduct.process}
        productType={firstProduct.productType}
        authenticated={authenticated}
        cartProductOwner={cartProductOwner}
        firstProductId={firstProduct.id}
        hasWebhosting={hasWebhosting}
      />

      <ProductsList>
        {productItems.map(product => {
          const ProductComponent = getProductComponent(product.component);

          return (
            <ProductContext.Provider value={product}>
              <ProductComponent
                product={product}
                key={`product-${getProductKey(product)}`}
              />
            </ProductContext.Provider>
          );
        })}
        {/** TODO: Implement new style => https://gitlab.corp.gandi.net/gandi/squad-shop/app-shop/-/issues/375 */}
        {showDomainBundles && (
          <ListLegacy
            variant="compact"
            itemId={getBundleKey}
            items={cartDomainBundles}
          >
            {renderDomainBundle}
          </ListLegacy>
        )}
      </ProductsList>

      {miscFees && (
        <DomainMiscFeesRow
          count={miscFees.count}
          price={miscFees.priceBeforeTaxes}
        />
      )}
    </ProductKindBlock>
  );
}

function ProductKindBlock({
  children,
  id,
}: {
  children: React.Node,
  id: string,
}): React.MixedElement {
  return (
    <Base
      id={id}
      position="relative"
      backgroundColor="surface"
      className={styles.root}
    >
      {children}
    </Base>
  );
}
